// Imported by utils/pageContext.ts, which also ships to Client Components: keep this module free of
// runtime imports that pull in the Statsig SDK or next/server.
import type { NextResponse } from 'next/server';
import { createCookieOptions, REDIRECT_EXPOSURE_COOKIE } from 'utils/cookies';
import { normalizePath } from 'utils/normalizePath';
import { logRedirectEvent } from './redirectLog';
import type { RedirectRule } from './types';

export const REDIRECT_EXPOSURE_HEADER = 'x-redirect-exposure';
const COOKIE_MAX_AGE_SECONDS = 60;
// Browsers drop a cookie over ~4KB silently, which would lose the exposure without a trace.
const MAX_COOKIE_VALUE_LENGTH = 3500;

export type RedirectTestArm = 'control' | 'treatment-1';
export const REDIRECT_TEST_ARMS: readonly RedirectTestArm[] = ['control', 'treatment-1'];
export const REDIRECT_TREATMENT_ARM: RedirectTestArm = 'treatment-1';

/** The source request's Statsig context; an absent field was absent when the proxy bucketed. */
export interface RedirectExposureContext {
  url: string;
  referrer?: string;
  segmentAnonymousId?: string;
}

export interface RedirectExposure {
  experimentId: string;
  variantName: RedirectTestArm;
  context: RedirectExposureContext;
}

interface RedirectExposureHandoff extends RedirectExposure {
  destinationPath: string;
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters
export function isRedirectTestArm(value: unknown): value is RedirectTestArm {
  return REDIRECT_TEST_ARMS.some(arm => arm === value);
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters
function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters
function toRedirectExposure(value: unknown): RedirectExposure | undefined {
  if (!isRecord(value) || !isRecord(value.context)) return undefined;
  const { experimentId, variantName } = value;
  const { url, referrer, segmentAnonymousId } = value.context;
  if (typeof experimentId !== 'string' || !experimentId || !isRedirectTestArm(variantName)) return undefined;
  if (typeof url !== 'string' || !isOptionalString(referrer) || !isOptionalString(segmentAnonymousId)) {
    return undefined;
  }
  return { experimentId, variantName, context: { url, referrer, segmentAnonymousId } };
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

/** Reads the proxy's handoff header; anything that doesn't parse as an exposure is ignored. */
export function parseRedirectExposure(value: string | null | undefined): RedirectExposure | undefined {
  if (!value) return undefined;
  try {
    return toRedirectExposure(parseJson(decodeURIComponent(value)));
  } catch {
    return undefined;
  }
}

// Percent-encoded because Headers.set throws on characters above U+00FF, which a decoded cookie value can contain.
export function serializeRedirectExposure(exposure: RedirectExposure): string {
  return encodeURIComponent(JSON.stringify(exposure));
}

/** Hands a treatment exposure across the 302 to the destination, where the proxy consumes it. */
export function setRedirectExposureCookie(
  response: NextResponse,
  exposure: RedirectExposure,
  destinationPath: string,
): void {
  const handoff: RedirectExposureHandoff = { ...exposure, destinationPath };
  const value = JSON.stringify(handoff);
  if (encodeURIComponent(value).length > MAX_COOKIE_VALUE_LENGTH) {
    logRedirectEvent({
      event: 'redirect.exposureHandoff',
      outcome: 'skipped',
      reason: 'too-large',
      statsigExperimentId: exposure.experimentId,
    });
    return;
  }

  response.cookies.set(
    REDIRECT_EXPOSURE_COOKIE,
    value,
    createCookieOptions({
      // Host-only and path-scoped so only the destination page ever receives it.
      cookieOptions: { domain: undefined, path: destinationPath, maxAge: COOKIE_MAX_AGE_SECONDS, httpOnly: true },
    }),
  );
  logRedirectEvent({
    event: 'redirect.exposureHandoff',
    outcome: 'set',
    transport: 'cookie',
    statsigExperimentId: exposure.experimentId,
  });
}

// Deletes at the Path the handoff was set on: a trailing-slash 301 can change the request path, and a mismatched Path
// leaves the cookie in place. Only a path matching the request path up to slash and case is trusted.
export function clearRedirectExposureCookie(response: NextResponse, value: string, requestPath: string): void {
  const parsed = parseJson(value);
  const setPath = isRecord(parsed) ? parsed.destinationPath : undefined;
  const path =
    typeof setPath === 'string' && normalizePath(setPath) === normalizePath(requestPath) ? setPath : requestPath;
  response.cookies.set(
    REDIRECT_EXPOSURE_COOKIE,
    '',
    createCookieOptions({
      // An epoch Expires, not just Max-Age=0: Next re-emits proxy cookies through a parser that drops falsy
      // attributes, which would turn a Max-Age-only delete into an empty session cookie.
      cookieOptions: { domain: undefined, path, maxAge: 0, expires: new Date(0), httpOnly: true },
    }),
  );
}

/**
 * Accepts the handoff only on its destination and only while a served redirect test still points there, so a stale
 * or replayed cookie can't attribute an exposure to the wrong page.
 */
export function consumeRedirectExposureCookie(
  value: string,
  pathname: string,
  redirects: readonly RedirectRule[],
): RedirectExposure | undefined {
  const parsed = parseJson(value);
  const exposure = toRedirectExposure(parsed);
  const destinationPath = isRecord(parsed) ? parsed.destinationPath : undefined;
  if (!exposure || exposure.variantName !== REDIRECT_TREATMENT_ARM || typeof destinationPath !== 'string') {
    logRedirectEvent({ event: 'redirect.exposureHandoff', outcome: 'rejected', reason: 'malformed' });
    return undefined;
  }

  const target = normalizePath(destinationPath);
  const statsigExperimentId = exposure.experimentId;
  if (normalizePath(pathname) !== target) {
    logRedirectEvent({
      event: 'redirect.exposureHandoff',
      outcome: 'rejected',
      reason: 'path-mismatch',
      statsigExperimentId,
    });
    return undefined;
  }
  const isServedTest = redirects.some(
    rule => rule.statsigExperimentId === statsigExperimentId && normalizePath(rule.destination) === target,
  );
  if (!isServedTest) {
    logRedirectEvent({
      event: 'redirect.exposureHandoff',
      outcome: 'rejected',
      reason: 'unknown-test',
      statsigExperimentId,
    });
    return undefined;
  }

  logRedirectEvent({ event: 'redirect.exposureHandoff', outcome: 'consumed', statsigExperimentId });
  return exposure;
}
